import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, ExternalLink, Image as ImageIcon } from "lucide-react";
import {
  projectContentApi,
  ContentInput,
  ContentKind,
  ProjectContent,
  ApplicationStatus,
  ClientDecision,
} from "../../backend/api/projectContent";
import {
  useAccessibleProjects,
  useContentAction,
  useProjectContent,
} from "../../backend/queries/projectContent";
import { useCurrentUser } from "../../backend/queries/auth";
import {
  Page,
  PageHeader,
  PageSection,
  FilterBar,
  FilterField,
  DataView,
  EmptyState,
  ErrorState,
  LoadingState,
  StatusBadge,
} from "../../components/patterns";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";

const statuses: Record<ApplicationStatus, string> = {
  shortlisted: "Shortlisted",
  with_agent: "Application with agent",
  approved: "Approved",
  declined: "Declined",
  other: "Other",
};
const decisions: Record<ClientDecision, string> = {
  pending: "Awaiting decision",
  proceed: "Proceed with application",
  not_proceeding: "Client not proceeding",
};
const selectClass =
  "h-10 rounded-md border border-input bg-background px-3 text-sm";
const blank = (kind: ContentKind): ContentInput => ({
  kind,
  title: "",
  description: "",
  internal_notes: "",
  listing_url: "",
  viewing_url: "",
  visible_to_client: false,
  application_status: "shortlisted",
  status_note: "",
});

export default function ProjectContentPage({ kind }: { kind: ContentKind }) {
  const projects = useAccessibleProjects();
  const [params, setParams] = useSearchParams();
  const project =
    params.get("projectId") || String(projects.data?.[0]?.id || "");
  const content = useProjectContent(project);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const { data: me } = useCurrentUser();
  const user = me?.profile || me;
  const staff = (user?.role === "Owner" || user?.role === "Admin") || user?.role === "Agent";
  const title =
    kind === "property" ? "Properties & viewings" : "Project updates";
  const rows = (content.data || []).filter(
    (item) =>
      item.kind === kind &&
      `${item.title} ${item.description}`
        .toLowerCase()
        .includes(search.toLowerCase()),
  );
  const entry = content.data?.find((item) => item.id === selected);
  return (
    <Page>
      <PageHeader
        title={title}
        description={
          kind === "property"
            ? "Explore properties, watch viewings and let your team know what you think."
            : "Photos, videos and news from your move."
        }
        actions={
          project &&
          (staff || kind === "property") && (
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              {kind === "property" ? "Add property" : "Post update"}
            </Button>
          )
        }
      />
      <div className="flex gap-4 text-sm">
        <Link
          className="underline underline-offset-4"
          to={`/properties?projectId=${project}`}
        >
          Properties & viewings
        </Link>
        <Link
          className="underline underline-offset-4"
          to={`/project-updates?projectId=${project}`}
        >
          Project updates
        </Link>
      </div>
      {projects.isPending ? (
        <LoadingState />
      ) : projects.isError ? (
        <ErrorState onRetry={() => projects.refetch()} />
      ) : !projects.data?.length ? (
        <EmptyState
          title="No projects yet"
          description="Your team will share your project here."
        />
      ) : (
        <>
          <FilterBar
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder={`Search ${kind === "property" ? "properties" : "updates"}`}
          >
            <FilterField label="Project">
              {(controlId) => (
                <select
                  id={controlId}
                  className={selectClass}
                  aria-label="Project"
                  value={project}
                  onChange={(e) => {
                    setParams({ projectId: e.target.value });
                    setSelected(null);
                    setCreating(false);
                  }}
                >
                  {projects.data.map((p) => (
                    <option key={p.id} value={String(p.id)}>
                      {p.relocating_from || "Project"} →{" "}
                      {p.relocating_to || `#${p.id}`}
                    </option>
                  ))}
                </select>
              )}
            </FilterField>
          </FilterBar>
          <DataView
            caption={title}
            rows={rows}
            getRowKey={(r) => r.id}
            status={
              content.isPending
                ? "loading"
                : content.isError
                  ? "error"
                  : "ready"
            }
            onRetry={() => content.refetch()}
            empty={
              <EmptyState
                title={
                  kind === "property"
                    ? "Your shortlist starts here"
                    : "No updates yet"
                }
                description={
                  kind === "property"
                    ? "Add a listing you like, or wait for your team to share a property."
                    : "Your team will share photos and videos as your move progresses."
                }
              />
            }
            columns={[
              {
                id: "title",
                header: kind === "property" ? "Property" : "Update",
                primary: true,
                cell: (r) => (
                  <div className="flex items-center gap-3">
                    {r.media.find((m) => m.mime_type.startsWith("image/")) ? (
                      <img
                        className="h-14 w-20 rounded-md object-cover"
                        alt=""
                        src={projectContentApi.mediaUrl(
                          project,
                          r.id,
                          r.media.find((m) => m.mime_type.startsWith("image/"))!
                            .id,
                        )}
                      />
                    ) : (
                      <div className="flex h-14 w-20 shrink-0 items-center justify-center rounded-md bg-muted">
                        <ImageIcon className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}
                    <button
                      className="text-left font-medium underline-offset-4 hover:underline"
                      onClick={() => setSelected(r.id)}
                    >
                      {r.title}
                    </button>
                  </div>
                ),
              },
              ...(kind === "property"
                ? [
                    {
                      id: "decision",
                      header: "Client decision",
                      cell: (r: ProjectContent) => (
                        <span className="text-sm">
                          {decisions[r.client_decision]}
                        </span>
                      ),
                    },
                    {
                      id: "status",
                      header: "Application",
                      cell: (r: ProjectContent) => (
                        <StatusBadge
                          tone={
                            r.application_status === "approved"
                              ? "success"
                              : r.application_status === "declined"
                                ? "danger"
                                : "neutral"
                          }
                        >
                          {statuses[r.application_status]}
                        </StatusBadge>
                      ),
                    },
                  ]
                : [
                    {
                      id: "date",
                      header: "Posted",
                      cell: (r: ProjectContent) =>
                        new Date(r.created_at).toLocaleDateString(),
                    },
                  ]),
              ...(staff
                ? [
                    {
                      id: "visibility",
                      header: "Visibility",
                      cell: (r: ProjectContent) => (
                        <StatusBadge
                          tone={r.visible_to_client ? "success" : "neutral"}
                        >
                          {r.visible_to_client
                            ? "Shared with client"
                            : "Staff draft"}
                        </StatusBadge>
                      ),
                    },
                  ]
                : []),
            ]}
            rowActions={(r) => (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelected(r.id)}
              >
                View
              </Button>
            )}
          />
        </>
      )}
      <Dialog
        open={creating || !!entry}
        onOpenChange={(open) => {
          if (!open) {
            setCreating(false);
            setSelected(null);
          }
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {entry?.title ||
                (kind === "property"
                  ? "Add a property"
                  : "Post a project update")}
            </DialogTitle>
            <DialogDescription>
              {staff
                ? "Drafts stay private until you share them with the client."
                : kind === "property"
                  ? "Share a suggestion or tell your team whether you would like to proceed."
                  : "An update from your relocation team."}
            </DialogDescription>
          </DialogHeader>
          {(creating || entry) && (
            <ContentDetail
              key={entry?.id || `new-${project}-${kind}`}
              project={project}
              kind={kind}
              entry={entry}
              staff={staff}
              userId={user?.id || ""}
              onCreated={(id) => {
                setCreating(false);
                setSelected(id);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Page>
  );
}
function ContentDetail({
  project,
  kind,
  entry,
  staff,
  userId,
  onCreated,
}: {
  project: string;
  kind: ContentKind;
  entry?: ProjectContent;
  staff: boolean;
  userId: string;
  onCreated: (id: string) => void;
}) {
  const action = useContentAction(project);
  const [editing, setEditing] = useState(!entry);
  const [draft, setDraft] = useState<ContentInput>(
    entry
      ? {
          kind: entry.kind,
          title: entry.title,
          description: entry.description,
          internal_notes: entry.internal_notes || "",
          listing_url: entry.listing_url,
          viewing_url: entry.viewing_url,
          visible_to_client: entry.visible_to_client,
          application_status: entry.application_status,
          status_note: entry.status_note,
        }
      : blank(kind),
  );
  const [note, setNote] = useState(entry?.decision_note || "");
  const [localError, setLocalError] = useState("");
  const [success, setSuccess] = useState("");
  const field = (key: keyof ContentInput, value: string | boolean) =>
    setDraft((prev) => ({ ...prev, [key]: value }));
  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSuccess("");
    try {
      const result = await action.mutateAsync(() =>
        entry
          ? projectContentApi.update(project, entry.id, draft)
          : projectContentApi.create(project, draft),
      );
      setEditing(false);
      onCreated(result.id);
      setSuccess("Saved. You can now add photos or a video.");
    } catch {
      /* mutation renders error */
    }
  }
  async function decision(value: ClientDecision) {
    setSuccess("");
    try {
      await action.mutateAsync(() =>
        projectContentApi.decide(project, entry!.id, value, note),
      );
      setSuccess("Your decision has been shared with your team.");
    } catch {}
  }
  async function upload(file?: File) {
    if (!file || !entry) return;
    setLocalError("");
    setSuccess("");
    if (file.size > 50 * 1024 * 1024) {
      setLocalError(
        "Choose a file smaller than 50 MB, or add a OneDrive link.",
      );
      return;
    }
    try {
      await action.mutateAsync(() =>
        projectContentApi.upload(project, entry.id, file),
      );
      setSuccess("Upload complete.");
    } catch {}
  }
  return (
    <div className="space-y-5">
      {(action.isError || localError) && (
        <p role="alert" className="text-sm text-destructive">
          {localError || action.error?.message}
        </p>
      )}
      {success && (
        <p role="status" className="text-sm text-muted-foreground">
          {success}
        </p>
      )}
      {editing ? (
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="content-title">
              {kind === "property"
                ? "Property address or title"
                : "Update title"}
            </Label>
            <Input
              id="content-title"
              required
              maxLength={200}
              value={draft.title}
              onChange={(e) => field("title", e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="content-description">
              {kind === "property" ? "About this property" : "Update"}
            </Label>
            <Textarea
              id="content-description"
              maxLength={10000}
              value={draft.description}
              onChange={(e) => field("description", e.target.value)}
            />
          </div>
          {kind === "property" && (
            <div className="space-y-1">
              <Label htmlFor="listing-url">Listing link</Label>
              <Input
                id="listing-url"
                type="url"
                placeholder="https://"
                maxLength={4096}
                value={draft.listing_url}
                onChange={(e) => field("listing_url", e.target.value)}
              />
            </div>
          )}
          <div className="space-y-1">
            <Label htmlFor="viewing-url">
              {kind === "property"
                ? "Viewing / OneDrive link"
                : "Video / OneDrive link"}
            </Label>
            <Input
              id="viewing-url"
              type="url"
              placeholder="https://"
              maxLength={4096}
              value={draft.viewing_url}
              onChange={(e) => field("viewing_url", e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Optional. You can upload photos or an MP4 after saving. External
              links open in a new tab.
            </p>
          </div>
          {staff && (
            <>
              {kind === "property" && (
                <>
                  <div className="space-y-1">
                    <Label htmlFor="application-status">
                      Application status
                    </Label>
                    <select
                      id="application-status"
                      className={`${selectClass} w-full`}
                      value={draft.application_status}
                      onChange={(e) =>
                        field("application_status", e.target.value)
                      }
                    >
                      {Object.entries(statuses).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="status-note">
                      Status explanation{" "}
                      {draft.application_status === "other"
                        ? "(required)"
                        : "(optional)"}
                    </Label>
                    <Input
                      id="status-note"
                      required={draft.application_status === "other"}
                      maxLength={2000}
                      value={draft.status_note}
                      onChange={(e) => field("status_note", e.target.value)}
                    />
                  </div>
                </>
              )}
              <div className="space-y-1">
                <Label htmlFor="internal-notes">
                  Internal notes · staff only
                </Label>
                <Textarea
                  id="internal-notes"
                  maxLength={10000}
                  value={draft.internal_notes}
                  onChange={(e) => field("internal_notes", e.target.value)}
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={draft.visible_to_client}
                  onChange={(e) => field("visible_to_client", e.target.checked)}
                />
                Share with client
              </label>
            </>
          )}
          <div className="flex gap-2">
            <Button type="submit" disabled={action.isPending}>
              {action.isPending
                ? "Saving…"
                : entry
                  ? "Save changes"
                  : staff
                    ? "Save"
                    : "Add property"}
            </Button>
            {entry && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditing(false)}
              >
                Cancel
              </Button>
            )}
          </div>
        </form>
      ) : (
        entry && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              {kind === "property" && (
                <StatusBadge
                  tone={
                    entry.application_status === "approved"
                      ? "success"
                      : "neutral"
                  }
                >
                  {statuses[entry.application_status]}
                </StatusBadge>
              )}
              {staff && (
                <StatusBadge>
                  {entry.visible_to_client
                    ? "Shared with client"
                    : "Staff draft"}
                </StatusBadge>
              )}
              <span className="text-xs text-muted-foreground">
                {new Date(entry.created_at).toLocaleDateString()}
              </span>
              {staff && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditing(true)}
                >
                  Edit
                </Button>
              )}
            </div>
            {entry.description && (
              <p className="whitespace-pre-wrap text-sm">{entry.description}</p>
            )}
            {entry.status_note && (
              <p className="text-sm text-muted-foreground">
                {entry.status_note}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              {entry.listing_url && (
                <Button asChild variant="outline">
                  <a
                    href={entry.listing_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open listing <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              )}
              {entry.viewing_url && (
                <Button asChild variant="outline">
                  <a
                    href={entry.viewing_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {kind === "property"
                      ? "Watch viewing"
                      : "Open video update"}{" "}
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              )}
            </div>
            {entry.media.length > 0 && (
              <PageSection title="Photos & videos">
                <div className="grid gap-3 sm:grid-cols-2">
                  {entry.media.map((m) => (
                    <div key={m.id} className="space-y-2">
                      {m.mime_type.startsWith("video/") ? (
                        <video
                          className="w-full rounded-md bg-muted"
                          controls
                          preload="metadata"
                          src={projectContentApi.mediaUrl(
                            project,
                            entry.id,
                            m.id,
                          )}
                          aria-label={m.name}
                        />
                      ) : (
                        <a
                          href={projectContentApi.mediaUrl(
                            project,
                            entry.id,
                            m.id,
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            className="h-44 w-full rounded-md object-cover"
                            src={projectContentApi.mediaUrl(
                              project,
                              entry.id,
                              m.id,
                            )}
                            alt={m.name}
                          />
                        </a>
                      )}
                      <p className="break-all text-xs text-muted-foreground">
                        {m.name}
                      </p>
                      {(staff || entry.created_by === userId) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={action.isPending}
                          onClick={() => {
                            void action
                              .mutateAsync(() =>
                                projectContentApi.removeMedia(
                                  project,
                                  entry.id,
                                  m.id,
                                ),
                              )
                              .catch(() => {});
                          }}
                        >
                          Remove attachment
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </PageSection>
            )}
            {(staff || entry.created_by === userId) && (
              <div className="space-y-2">
                <Label htmlFor="content-upload">Add photos or video</Label>
                <Input
                  id="content-upload"
                  type="file"
                  accept="image/jpeg,image/png,video/mp4"
                  disabled={action.isPending}
                  onChange={async (e) => {
                    const input = e.currentTarget;
                    await upload(input.files?.[0]);
                    input.value = "";
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  JPEG, PNG or MP4, up to 50 MB per file. Videos play as
                  uploaded; larger videos can use a OneDrive link.
                </p>
                {action.isPending && (
                  <p role="status" className="text-sm">
                    Saving…
                  </p>
                )}
              </div>
            )}
            {staff && entry.internal_notes && (
              <PageSection title="Internal notes · staff only">
                <p className="whitespace-pre-wrap text-sm">
                  {entry.internal_notes}
                </p>
              </PageSection>
            )}
            {kind === "property" && (
              <PageSection
                title={staff ? "Client decision" : "Would you like to proceed?"}
              >
                <p className="text-sm">{decisions[entry.client_decision]}</p>
                {entry.decision_note && (
                  <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                    {entry.decision_note}
                  </p>
                )}
                {!staff && (
                  <>
                    <Label htmlFor="decision-note">
                      Message to your team (optional)
                    </Label>
                    <Textarea
                      id="decision-note"
                      maxLength={2000}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                    />
                    <div className="flex flex-wrap gap-2">
                      <Button
                        disabled={action.isPending}
                        onClick={() => decision("proceed")}
                      >
                        Proceed with application
                      </Button>
                      <Button
                        variant="outline"
                        disabled={action.isPending}
                        onClick={() => decision("not_proceeding")}
                      >
                        Not interested
                      </Button>
                      {entry.client_decision !== "pending" && (
                        <Button
                          variant="ghost"
                          disabled={action.isPending}
                          onClick={() => decision("pending")}
                        >
                          Still deciding
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </PageSection>
            )}
          </>
        )
      )}
    </div>
  );
}
